import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Box, HStack, Image, Pressable, VStack, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';

import { SwitchButton, Text } from '@pimm/base';
import { fmtLastSeen, getBatteryLabel, getBatteryNote } from '../_helper';
import { BatteryLevel, HubMeta, SensorMeta } from '../types';
import { LinkIcon } from '../icons/link';
import { EnableDisableSensor } from './sensor-setting-confirmation';

/* ── Badges ─────────────────────────────────────────────────────────────── */

type ConnStatus = 'online' | 'offline' | 'disabled';

const CONN_STYLE: Record<ConnStatus, { bg: string; text: string; border?: string }> = {
  online: { bg: 'gray.100', text: 'gray.600' },
  offline: { bg: 'error.100', text: 'error.700' },
  disabled: { bg: 'transparent', text: 'gray.500', border: 'gray.400' },
};

const ConnBadge = ({ status, label }: { status: ConnStatus; label: string }) => {
  const s = CONN_STYLE[status];
  return (
    <Box bg={s.bg} rounded="md" px={2.5} py={1} borderWidth={s.border ? 1 : 0} borderColor={s.border}>
      <Text size="xs" fontWeight={600} color={s.text}>
        {label}
      </Text>
    </Box>
  );
};

const BATTERY_STYLE: Record<BatteryLevel, { bg: string; text: string }> = {
  ok: { bg: 'gray.100', text: 'gray.600' },
  info: { bg: 'warning.100', text: 'warning.700' },
  warning: { bg: 'orange.100', text: 'orange.700' },
  alarm: { bg: 'error.100', text: 'error.700' },
  dead: { bg: 'gray.900', text: 'white' },
};

const BatteryBadge = ({ level, model, t }: { level: BatteryLevel; model?: string; t: ReturnType<typeof useTranslation>['t'] }) => {
  const s = BATTERY_STYLE[level] ?? BATTERY_STYLE.ok;
  return (
    <Box bg={s.bg} rounded="md" px={2.5} py={1}>
      <Text size="xs" fontWeight={600} color={s.text}>
        {t(getBatteryLabel(level, model))}
      </Text>
    </Box>
  );
};

/* ── CopyBtn ─────────────────────────────────────────────────────────────── */

const CopyBtn = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    try {
      navigator.clipboard?.writeText(text);
    } catch {
      /* clipboard unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <Pressable onPress={handleCopy} p={1} rounded="md" _hover={{ bg: 'gray.100' }}>
      {copied ? <Check size={14} color="#16A34A" /> : <Copy size={14} color="#98A2B3" />}
    </Pressable>
  );
};

/* ── Field ───────────────────────────────────────────────────────────────── */

const Field = ({ label, value }: { label: string; value?: string }) => (
  <VStack space={0.5}>
    <Text size="xs" color="gray.500" fontWeight={500}>
      {label}
    </Text>
    <Text size="md" fontWeight={700} color="gray.900">
      {value || '—'}
    </Text>
  </VStack>
);

/* ── SensorInfoCard ──────────────────────────────────────────────────────── */

type SensorInfoCardProps =
  | {
      kind: 'sensor';
      name: string;
      meta: SensorMeta;
      canEdit?: boolean;
      onRequestToggle?: (sensor: EnableDisableSensor) => void;
    }
  | {
      kind: 'hub';
      name: string;
      meta: HubMeta;
      canEdit?: never;
      onRequestToggle?: never;
    };

export const SensorInfoCard = (props: SensorInfoCardProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { kind, name, meta } = props;

  const imgUrl = meta.imgUrl;
  const serial = meta.serial;
  const model = meta.model;
  const copyText = [name, model, serial].filter(Boolean).join('\n');

  if (kind === 'hub') {
    const hub = meta as HubMeta;

    return (
      <VStack p={3} bg="white">
        <VStack p={2} space={2} borderWidth={1} borderColor="gray.200" rounded="lg">
          {hub.isPrimary && (
            <Box bg="blue.50" rounded="full" px={2} py={0.5} alignSelf="flex-start">
              <Text size="xs" fontWeight={700} color="blue.700" textTransform="uppercase">
                {t('store_equipment:primary')}
              </Text>
            </Box>
          )}

          <HStack justifyContent="space-between" alignItems="flex-start">
            <Text size="xl" fontWeight={700} color="gray.900" flex={1} numberOfLines={2}>
              {name}
            </Text>
            <CopyBtn text={copyText} />
          </HStack>

          <Box h={120} alignItems="center" justifyContent="center">
            {imgUrl ? (
              <Image source={{ uri: imgUrl }} style={{ width: '100%', height: '100%' }} alt={name} resizeMode="contain" />
            ) : (
              <Text size="sm" color="gray.400">
                {t('store_equipment:no_image_available')}
              </Text>
            )}
          </Box>

          {!!model && <Field label={t('store_equipment:model')} value={model} />}
          <Field label={t('store_equipment:serial')} value={serial} />

          <HStack space={6}>
            {hub.online !== undefined && (
              <VStack space={1}>
                <Text size="xs" color="gray.500" fontWeight={500}>
                  {t('store_equipment:status')}
                </Text>
                <ConnBadge status={hub.online ? 'online' : 'offline'} label={t(`store_equipment:${hub.online ? 'online' : 'offline'}`)} />
              </VStack>
            )}
            <VStack space={1}>
              <Text size="xs" color="gray.500" fontWeight={500}>
                {t('store_equipment:last_update')}
              </Text>
              <Text size="md" color="gray.700" fontWeight={600}>
                {fmtLastSeen(hub.lastReportedTime)}
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </VStack>
    );
  }

  const sensor = meta as SensorMeta;
  const isDoor = sensor.sensorType === 'door';
  const isEnabled = sensor.enabled;
  const connStatus: ConnStatus = !isEnabled ? 'disabled' : !sensor.isOnline ? 'offline' : 'online';
  const batteryNoteKey =
    sensor.batteryLevel !== undefined && (['warning', 'alarm', 'dead'] as BatteryLevel[]).includes(sensor.batteryLevel)
      ? getBatteryNote(sensor.model)
      : undefined;

  return (
    <VStack p={3} space={3} bg="white" rounded="lg">
      <VStack borderWidth={1} borderColor="gray.200" rounded="md" space={3} p={2}>
        <HStack justifyContent="space-between" alignItems="flex-start">
          <Text size="xl" fontWeight={700} color="gray.900" flex={1} numberOfLines={2}>
            {name}
          </Text>
          <CopyBtn text={copyText} />
        </HStack>
        <Box h={120} alignItems="center" justifyContent="center">
          {imgUrl ? (
            <Image source={{ uri: imgUrl }} style={{ width: '100%', height: '100%' }} alt={name} resizeMode="contain" />
          ) : (
            <Text size="sm" color="gray.400">
              {t('store_equipment:no_image_available')}
            </Text>
          )}
        </Box>

        {!!model && <Field label={t('store_equipment:model')} value={model} />}
        <Field label={t('store_equipment:serial')} value={serial} />

        {!!sensor.linkedTsu && (
          <HStack space={1.5} alignItems="flex-start" bg="blue.50" rounded="lg" borderWidth={1} borderColor="blue.100" px={2.5} py={2}>
            <Box mt={0.5}>
              <LinkIcon size="xs" color={colors.blue[600]} />
            </Box>
            <Text size="xs" color="blue.700" flex={1} lineHeight="sm">
              External probe connected to{' '}
              <Text size="xs" color="blue.700" fontWeight={700}>
                {sensor.linkedTsu}
              </Text>{' '}
              sensor
            </Text>
          </HStack>
        )}

        <HStack space={6}>
          <VStack space={1}>
            <Text size="xs" color="gray.500" fontWeight={500}>
              {t('store_equipment:status')}
            </Text>
            <ConnBadge status={connStatus} label={t(`store_equipment:${connStatus}`)} />
          </VStack>
          {sensor.batteryLevel !== undefined && (
            <VStack space={1}>
              <Text size="xs" color="gray.500" fontWeight={500}>
                {t('store_equipment:battery')}
              </Text>
              <BatteryBadge level={sensor.batteryLevel} model={sensor.model} t={t} />
            </VStack>
          )}
          <VStack space={1}>
            <Text size="xs" color="gray.500" fontWeight={500}>
              {t('store_equipment:last_update')}
            </Text>
            <Text size="md" color="gray.700" fontWeight={600}>
              {fmtLastSeen(sensor.lastSeen)}
            </Text>
          </VStack>
        </HStack>
        {!!batteryNoteKey && (
          <HStack space={1.5} alignItems="flex-start" bg="amber.50" rounded="lg" borderWidth={1} borderColor="amber.100" px={2.5} py={2}>
            <Text size="xs" color="amber.700" flex={1} lineHeight="sm">
              {t(batteryNoteKey)}
            </Text>
          </HStack>
        )}
      </VStack>

      <HStack justifyContent="space-between" alignItems="center" px={1}>
        <Text size="md" fontWeight={700} color="gray.900">
          {isEnabled ? t('store_equipment:enabled') : t('store_equipment:disabled')}
        </Text>
        <SwitchButton
          isChecked={isEnabled}
          isDisabled={!props.canEdit}
          color={!props.canEdit ? colors.gray[100] : isEnabled ? colors.success[400] : colors.error[400]}
          onChange={() =>
            props.onRequestToggle?.({
              isEnabled,
              isDoor,
              sensorName: name,
              instanceId: sensor.instanceId,
              equipmentId: sensor.equipmentId,
            })
          }
        />
      </HStack>
    </VStack>
  );
};
